import { useEffect, useCallback } from 'react';
import { useCustomerAuth } from '@/components/customer/CustomerAuthProvider';

/**
 * Hook to manage long-lived portal sessions with automatic refresh
 */
export const usePortalSession = () => {
  const { refreshSession, signOut } = useCustomerAuth();

  // Auto-refresh on app visibility change (when user comes back to app)
  const handleVisibilityChange = useCallback(async () => {
    if (document.visibilityState === 'visible') {
      const storedSession = localStorage.getItem('customer_session');
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          const expiresAt = new Date(sessionData.expires_at);
          const refreshExpiresAt = new Date(sessionData.refresh_expires_at);
          
          // If refresh token is expired, sign out
          if (refreshExpiresAt <= new Date()) {
            console.log('Refresh token expired, signing out');
            await signOut();
            return;
          }
          
          // If access token expires within 5 minutes, refresh it
          if (expiresAt.getTime() - Date.now() < 5 * 60 * 1000) {
            console.log('Access token expiring soon, refreshing...');
            const result = await refreshSession();
            if (result.error) {
              console.error('Failed to refresh session:', result.error);
              // Don't auto-logout on refresh failure, let user try again
            }
          }
        } catch (error) {
          console.error('Error checking session for refresh:', error);
          localStorage.removeItem('customer_session');
        }
      }
    }
  }, [refreshSession, signOut]);

  // Set up visibility change listener and periodic checks
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check on focus
    window.addEventListener('focus', handleVisibilityChange);
    
    // Periodic session check every 15 minutes
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        handleVisibilityChange();
      }
    }, 15 * 60 * 1000);

    // Initial check
    handleVisibilityChange();
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [handleVisibilityChange]);

  return {
    refreshSession,
  };
};